import {
    Experiment,
    ExperimentClient,
    ExperimentUser,
} from "@amplitude/experiment-js-client";
import { IAnalyticsEventTracker } from "utils/analytics";
import { UserActionEvent } from "utils/analytics/events";
import { generateAnalyticsUserID } from "utils/analytics/user-id";
import { IFeatureFlagValue, IFeatureManager } from "..";

export enum FeatureFlag {
    On = "on",
    Off = "off",
}

export const globalFeatureFlagUser = {
    email: "feature-flag@prelib.com",
    analyticsUserId: "3cea6092-a817-5a68-9cd3-9cfa6ca22aff",
} as const;

export class AmplitudeFlagAndExperimentManager implements IFeatureManager {
    private _experimentClient: ExperimentClient;
    private readonly listeners = new Set<() => void>();
    private stateVersion = 0;
    private fallbackFlagsEnabled = false;

    public constructor(
        private readonly deploymentKey: string,
        private readonly experimentServerUrl: string,
        private readonly enableDebugger: boolean = false,
        private readonly eventTracker?: IAnalyticsEventTracker
    ) {
        if (!deploymentKey) {
            throw new Error("deploymentKey");
        }

        this._experimentClient = Experiment.initialize(this.deploymentKey, {
            debug: this.enableDebugger,
            serverUrl: this.experimentServerUrl,
            flagsServerUrl: this.experimentServerUrl,
            fetchOnStart: false,
            automaticExposureTracking: !!this.eventTracker,
            exposureTrackingProvider: {
                track: (exposure) => {
                    this.eventTracker?.trackEvent(
                        UserActionEvent.AmplitudeVariantExposure,
                        exposure
                    );
                },
            },
        });
    }

    public async start(): Promise<boolean> {
        try {
            await this._experimentClient.start();

            return true;
        } catch (error) {
            console.error("Cannot start Amplitude SDK");

            return false;
        }
    }

    public fetchGlobalFlags(): Promise<void> {
        return this.fetchFlags(globalFeatureFlagUser.analyticsUserId);
    }

    public fetchUserFlags(email: string): Promise<void> {
        return this.fetchFlags(generateAnalyticsUserID(email), {
            email: email.trim().toLowerCase(),
        });
    }

    public setFallbackFlagsEnabled(enabled: boolean): void {
        if (this.fallbackFlagsEnabled === enabled) {
            return;
        }

        this.fallbackFlagsEnabled = enabled;
        this.notifyListeners();
    }

    public subscribe(listener: () => void): () => void {
        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    }

    public getStateVersion(): number {
        return this.stateVersion;
    }

    public isFeatureFlagEnabled(name: string, expectedValue: string): boolean {
        const flagVariant = this.getFeatureFlag(name);

        return flagVariant?.value === expectedValue;
    }

    public getFeatureFlag(name: string): IFeatureFlagValue | null {
        if (this.fallbackFlagsEnabled) {
            return null;
        }

        const flagVariant = this._experimentClient.variant(name);
        if (!flagVariant.value) {
            return null;
        }

        return { value: flagVariant.value, payload: flagVariant.payload };
    }

    private async fetchFlags(
        userId: string,
        userProperties?: Record<string, string>
    ): Promise<void> {
        const experimentUser: ExperimentUser = {
            user_id: userId,
            ...(userProperties ? { user_properties: userProperties } : {}),
        };

        await this._experimentClient.fetch(experimentUser);
        this.fallbackFlagsEnabled = false;
        this.notifyListeners();
    }

    private notifyListeners(): void {
        this.stateVersion += 1;
        this.listeners.forEach((listener) => listener());
    }
}
